import type { ReactNode } from "react";
import { TextInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { OnlineOrderOrderedBy } from "@/lib/api/types";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import { OrderedByLine } from "./OrderedByLine";

/** The id of the head's «يلزم رقم الهاتف والعنوان قبل الإرسال» line. */
export const ORDER_CONTACT_MISSING_ID = "order-contact-missing";

/** The inline customer fields of an own delivery (the design's `cust`). */
export type DeliveryContactDraft = { name: string; phone: string; address: string };

export type OffPremiseHeadContact = {
  draft: DeliveryContactDraft;
  onChange: (field: keyof DeliveryContactDraft, value: string) => void;
  /** Leaving a field saves what changed (`editCustomer`). */
  onCommit: () => void;
  /** Phone or address still blank: the design's warning line under the fields. */
  missing: boolean;
  /** Read-only (no `INVOICE_APPEND`, or a bill that may not be corrected). */
  disabled: boolean;
};

/**
 * The PAPER order panel's head on a bill at NO table (B6 · P26, plan D-46,
 * the design's register panel for `fromDel`):
 *
 *   Invoice #n · N items
 *   [36px badge]  Delivery / Pickup (20/700)           Make pickup / Make delivery
 *                 Goes to the driver queue after the kitchen / Customer collects…
 *   [Customer name] [Phone *]
 *   [Delivery address *]
 *   Phone and address are needed before sending   ← while either is blank
 *
 * The badge is the courier on a delivery, the takeaway bag on a pickup. The
 * switch link is given only on an OPEN, direct (non-platform) bill for the
 * cashier's rung — `editCharges {channel}`, the same gates as `ChargesDialog`.
 * The fields are drawn only for an OWN delivery (no platform): the one bill
 * the server refuses to fire or close without a phone and an address (D-39).
 */
export function OffPremiseOrderHead({
  numberLine,
  channel,
  platformName,
  onSwitch,
  switchDisabled = false,
  contact,
  orderedBy = null,
  extra,
  dispatch,
  dispatchHead,
}: {
  /** "Invoice #n · N items", as the panel words it. */
  numberLine: ReactNode;
  channel: "DELIVERY" | "TAKEAWAY";
  /** A platform's bill: its name is the info line, and there is no switch. */
  platformName: string | null;
  onSwitch?: () => void;
  switchDisabled?: boolean;
  contact?: OffPremiseHeadContact;
  /**
   * B7 — «طلبه»: the orderer of an online delivery sent for someone else.
   * The fields above hold the RECIPIENT (the bill's contact); this names who
   * ordered it, phone masked. Null on every other bill.
   */
  orderedBy?: OnlineOrderOrderedBy | null;
  /** The fee / platform correction link, under the fields. */
  extra?: ReactNode;
  /**
   * An own delivery's driver line and «إسناد إلى مندوب» (`BillDispatchStrip`),
   * at the head's foot — under the fields it depends on (D-39).
   */
  dispatch?: ReactNode;
  /** The short dispatch chip and its icon control, on the number's line (2026-10-20). */
  dispatchHead?: ReactNode;
}) {
  const t = useTranslate();
  /* A fixed id (one order panel per screen): the foot's greyed «إسناد إلى
     مندوب» points its description at this line rather than repeating it. */
  const missingId = ORDER_CONTACT_MISSING_ID;
  const delivery = channel === "DELIVERY";
  // G24b L5: the blank required field is invalid and points at the line saying why.
  const phoneInvalid = Boolean(contact?.missing && !contact.draft.phone.trim());
  const addressInvalid = Boolean(contact?.missing && !contact.draft.address.trim());
  const info = platformName
    ? t((m) => m.dispatch.panelPlatformInfo, { platform: platformName })
    : delivery
      ? t((m) => m.dispatch.panelDeliveryInfo)
      : t((m) => m.dispatch.panelPickupInfo);

  return (
    <div
      data-ui="order-head"
      data-place="off-premise"
      data-channel={channel}
      data-testid="order-head-off-premise"
      className="flex shrink-0 flex-col"
    >
      <div data-ui="order-head-row" className="flex min-w-0 items-baseline">
        {numberLine}
        {dispatchHead ? <span className="flex min-w-0 flex-1 basis-0 justify-end self-center">{dispatchHead}</span> : null}
      </div>
      <div data-ui="order-head-place-row" className="flex items-center">
        <span data-ui="order-head-badge" className="flex shrink-0">
          <DoodleBadge id={delivery ? "courier" : "takeaway"} size={36} animate={false} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span data-ui="order-head-place" data-testid="order-head-place">
            {delivery ? t((m) => m.dispatch.panelDelivery) : t((m) => m.dispatch.panelPickup)}
          </span>
          <span data-ui="order-head-info" data-testid="order-head-info">
            <bdi>{info}</bdi>
          </span>
        </span>
        {onSwitch ? (
          <button
            type="button"
            data-ui="order-head-link"
            data-testid="order-switch-channel"
            disabled={switchDisabled}
            onClick={onSwitch}
            className="shrink-0 whitespace-nowrap disabled:opacity-40"
          >
            {delivery ? t((m) => m.dispatch.makePickup) : t((m) => m.dispatch.makeDelivery)}
          </button>
        ) : null}
      </div>
      {contact ? (
        <div data-ui="order-contact" data-testid="order-contact" className="flex flex-col">
          <div data-ui="order-contact-row" className="flex">
            <span data-ui="order-contact-field" className="flex min-w-0 flex-1">
              <TextInput
                name="customerName"
                data-testid="order-contact-name"
                autoComplete="off"
                dir="auto"
                value={contact.draft.name}
                disabled={contact.disabled}
                placeholder={t((m) => m.dispatch.contactName)}
                aria-label={t((m) => m.dispatch.contactName)}
                onChange={(event) => contact.onChange("name", event.target.value)}
                onBlur={contact.onCommit}
              />
            </span>
            <span data-ui="order-contact-field" className="flex min-w-0 flex-1">
              <TextInput
                name="customerPhone"
                data-testid="order-contact-phone"
                autoComplete="off"
                inputMode="tel"
                dir="ltr"
                required
                hasError={phoneInvalid}
                describedBy={contact.missing ? missingId : undefined}
                value={contact.draft.phone}
                disabled={contact.disabled}
                placeholder={t((m) => m.dispatch.contactPhone)}
                aria-label={t((m) => m.dispatch.contactPhone)}
                onChange={(event) => contact.onChange("phone", event.target.value)}
                onBlur={contact.onCommit}
              />
            </span>
          </div>
          <span data-ui="order-contact-field" className="flex min-w-0">
            <TextInput
              name="deliveryAddress"
              data-testid="order-contact-address"
              autoComplete="off"
              dir="auto"
              required
              hasError={addressInvalid}
              describedBy={contact.missing ? missingId : undefined}
              value={contact.draft.address}
              disabled={contact.disabled}
              placeholder={t((m) => m.dispatch.contactAddress)}
              aria-label={t((m) => m.dispatch.contactAddress)}
              onChange={(event) => contact.onChange("address", event.target.value)}
              onBlur={contact.onCommit}
            />
          </span>
          {orderedBy ? (
            <OrderedByLine orderedBy={orderedBy} testId="order-contact-ordered-by" />
          ) : null}
          {contact.missing ? (
            <span id={missingId} data-ui="order-contact-missing" data-testid="order-contact-missing" role="status">
              {t((m) => m.dispatch.contactMissing)}
            </span>
          ) : null}
          {extra}
        </div>
      ) : extra ? (
        <div className="flex">{extra}</div>
      ) : null}
      {dispatch}
    </div>
  );
}
